import { useCallback, useEffect, useState } from "react";
import { api, apiErrorMessage } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { CloudUpload, RefreshCw, Play, Download, Database, HardDriveDownload } from "lucide-react";
import { toast } from "sonner";

/**
 * Admin Settings card — nightly mongodump → S3 config + run-now + history.
 * Consumes /api/admin/backups/{config,run,history,<id>/download-url,restore}.
 */
export function BackupsCard() {
  const [cfg, setCfg] = useState(null);
  const [history, setHistory] = useState([]);
  const [busy, setBusy] = useState(false);
  const [running, setRunning] = useState(false);
  const [form, setForm] = useState({
    s3_bucket: "", s3_prefix: "prestige-backups", s3_region: "us-east-1",
    s3_endpoint_url: "", s3_access_key_id: "", s3_secret_access_key: "",
    retain_days: 30, enabled: true,
  });

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const [c, h] = await Promise.all([api.get("/admin/backups/config"), api.get("/admin/backups/history")]);
      setCfg(c.data);
      setHistory(h.data.items || []);
      setForm((f) => ({
        ...f,
        s3_bucket: c.data.s3_bucket || "",
        s3_prefix: c.data.s3_prefix || "prestige-backups",
        s3_region: c.data.s3_region || "us-east-1",
        s3_endpoint_url: c.data.s3_endpoint_url || "",
        retain_days: c.data.retain_days ?? 30,
        enabled: !!c.data.enabled,
        s3_access_key_id: "", s3_secret_access_key: "",
      }));
    } catch (e) {
      toast.error(apiErrorMessage(e) || "Could not load backups config");
    } finally { setBusy(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setBusy(true);
    try {
      // Only send keys if the admin actually typed something new (avoid clearing secrets)
      const body = { ...form };
      if (!form.s3_access_key_id) delete body.s3_access_key_id;
      if (!form.s3_secret_access_key) delete body.s3_secret_access_key;
      await api.post("/admin/backups/config", body);
      toast.success("Backup config saved");
      await load();
    } catch (e) {
      toast.error(apiErrorMessage(e) || "Save failed");
    } finally { setBusy(false); }
  };

  const runNow = async () => {
    setRunning(true);
    try {
      await api.post("/admin/backups/run", {});
      toast.success("Backup queued — refreshing every 4s…");
      // Poll history for the new row
      const target = history.length;
      for (let i = 0; i < 30; i++) {
        await new Promise((r) => setTimeout(r, 4000));
        const { data } = await api.get("/admin/backups/history");
        if ((data.items || []).length > target) {
          setHistory(data.items);
          toast.success("Backup complete");
          setRunning(false);
          return;
        }
      }
      toast.info("Still running — refresh in a minute.");
    } catch (e) {
      toast.error(apiErrorMessage(e) || "Could not queue backup");
    } finally { setRunning(false); }
  };

  const downloadRow = async (id) => {
    try {
      const { data } = await api.get(`/admin/backups/${id}/download-url`);
      window.open(data.url, "_blank", "noopener,noreferrer");
    } catch (e) {
      toast.error(apiErrorMessage(e) || "Could not sign download URL");
    }
  };

  const restoreRow = async (id) => {
    const typed = window.prompt(
      `⚠️ Restoring wipes and replaces the current database with the contents of backup ${id}.\n\nType RESTORE to confirm:`,
    );
    if (typed !== "RESTORE") return;
    setBusy(true);
    try {
      await api.post("/admin/backups/restore", { backup_id: id, confirm: "RESTORE" });
      toast.success("Database restored — reload the app.");
    } catch (e) {
      toast.error(apiErrorMessage(e) || "Restore failed");
    } finally { setBusy(false); }
  };

  return (
    <Card className="p-5 border-white/10 bg-card" data-testid="backups-card">
      <div className="flex items-start justify-between mb-3">
        <div>
          <div className="overline">Disaster recovery</div>
          <div className="text-lg font-semibold font-display mt-1 flex items-center gap-2">
            <Database className="h-4 w-4 text-orange-400" /> Automated backups
          </div>
          <div className="text-sm text-zinc-400 mt-1">
            Nightly <code className="text-xs">mongodump</code> → S3. Restores are one click on any backup below.
          </div>
        </div>
        <Button size="sm" variant="outline" onClick={load} disabled={busy} className="border-white/10" data-testid="backups-refresh">
          <RefreshCw className={"h-3.5 w-3.5 mr-1 " + (busy ? "animate-spin" : "")} /> Refresh
        </Button>
      </div>

      {cfg && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
          <div>
            <Label className="text-xs uppercase tracking-widest text-zinc-400">S3 bucket</Label>
            <Input data-testid="backup-bucket" value={form.s3_bucket} onChange={(e) => setForm({ ...form, s3_bucket: e.target.value })} placeholder="prestige-prod-backups" className="mt-1 bg-white/[0.04] border-white/10 text-white" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest text-zinc-400">Key prefix</Label>
            <Input data-testid="backup-prefix" value={form.s3_prefix} onChange={(e) => setForm({ ...form, s3_prefix: e.target.value })} className="mt-1 bg-white/[0.04] border-white/10 text-white" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest text-zinc-400">Region</Label>
            <Input data-testid="backup-region" value={form.s3_region} onChange={(e) => setForm({ ...form, s3_region: e.target.value })} className="mt-1 bg-white/[0.04] border-white/10 text-white" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest text-zinc-400">Endpoint URL (optional — for R2, B2, Wasabi…)</Label>
            <Input data-testid="backup-endpoint" value={form.s3_endpoint_url || ""} onChange={(e) => setForm({ ...form, s3_endpoint_url: e.target.value })} placeholder="https://s3.us-east-1.amazonaws.com" className="mt-1 bg-white/[0.04] border-white/10 text-white" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest text-zinc-400">Access key ID {cfg.s3_access_key_configured && <span className="text-emerald-400 text-[10px] ml-1">✓ saved</span>}</Label>
            <Input data-testid="backup-akid" value={form.s3_access_key_id} onChange={(e) => setForm({ ...form, s3_access_key_id: e.target.value })} placeholder={cfg.s3_access_key_configured ? "leave blank to keep saved value" : "AKIA…"} className="mt-1 bg-white/[0.04] border-white/10 text-white" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest text-zinc-400">Secret access key {cfg.s3_secret_configured && <span className="text-emerald-400 text-[10px] ml-1">✓ saved</span>}</Label>
            <Input data-testid="backup-sak" type="password" value={form.s3_secret_access_key} onChange={(e) => setForm({ ...form, s3_secret_access_key: e.target.value })} placeholder={cfg.s3_secret_configured ? "leave blank to keep saved value" : "…"} className="mt-1 bg-white/[0.04] border-white/10 text-white" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest text-zinc-400">Retention (days)</Label>
            <Input data-testid="backup-retain" type="number" min={1} value={form.retain_days} onChange={(e) => setForm({ ...form, retain_days: Number(e.target.value) })} className="mt-1 bg-white/[0.04] border-white/10 text-white" />
          </div>
          <div className="flex items-end justify-between md:col-span-1">
            <div>
              <Label className="text-xs uppercase tracking-widest text-zinc-400">Nightly backups</Label>
              <div className="text-[11px] text-zinc-500">Turn on for scheduled 02:00 UTC runs (cron).</div>
            </div>
            <Switch data-testid="backup-enabled" checked={form.enabled} onCheckedChange={(v) => setForm({ ...form, enabled: v })} />
          </div>
        </div>
      )}

      <div className="flex items-center justify-between mb-4">
        <Button
          size="sm" onClick={save} disabled={busy || !form.s3_bucket}
          className="bg-white text-black hover:bg-zinc-200 rounded-full"
          data-testid="backups-save"
        >
          Save config
        </Button>
        <Button
          size="sm" onClick={runNow}
          disabled={running || !cfg?.s3_bucket}
          className="bg-orange-500 hover:bg-orange-600 rounded-full"
          data-testid="backups-run-now"
        >
          <Play className={"h-3.5 w-3.5 mr-1 " + (running ? "animate-pulse" : "")} />
          {running ? "Running…" : "Run backup now"}
        </Button>
      </div>

      {history.length > 0 && (
        <div>
          <div className="text-[11px] uppercase tracking-widest text-zinc-500 mb-2">Recent backups</div>
          <div className="space-y-1">
            {history.map((r) => (
              <div key={r.id} className="flex items-center justify-between gap-3 text-xs border border-white/5 rounded-md px-3 py-2 bg-white/[0.02]" data-testid="backup-row">
                <div className="flex items-center gap-2 min-w-0">
                  <Badge variant="outline" className={r.status === "ok" ? "border-emerald-500/40 text-emerald-300 bg-emerald-500/10" : "border-red-500/40 text-red-300 bg-red-500/10"}>
                    {r.status}
                  </Badge>
                  <div className="min-w-0">
                    <div className="text-zinc-300 truncate">{r.s3_key || r.id}</div>
                    <div className="text-[10px] text-zinc-500">{((r.gz_size_bytes || 0) / (1024 * 1024)).toFixed(2)} MB · {r.duration_s}s · {r.actor_email}</div>
                  </div>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <Button size="sm" variant="ghost" onClick={() => downloadRow(r.id)} className="h-7 px-2 text-orange-300 hover:text-orange-200 hover:bg-white/[0.05]" data-testid={`backup-download-${r.id}`}>
                    <Download className="h-3.5 w-3.5" />
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => restoreRow(r.id)} className="h-7 px-2 text-amber-300 hover:text-amber-200 hover:bg-white/[0.05]" data-testid={`backup-restore-${r.id}`}>
                    <HardDriveDownload className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {history.length === 0 && cfg?.s3_bucket && (
        <div className="text-xs text-zinc-500 text-center py-6 rounded-md border border-dashed border-white/10">
          No backups yet. Click <b>Run backup now</b> to fire the first one.
        </div>
      )}

      {!cfg?.s3_bucket && (
        <div className="rounded-md border border-amber-500/20 bg-amber-500/[0.04] px-3 py-3 text-xs text-amber-200 flex items-start gap-2">
          <CloudUpload className="h-4 w-4 mt-0.5" />
          <div>
            <div className="font-semibold">Not configured</div>
            <div className="opacity-80">Set an S3 bucket + credentials above, hit <b>Save config</b>, then <b>Run backup now</b>. Cron wires up via <code className="text-[10px]">python /app/backend/backup.py backup</code>.</div>
          </div>
        </div>
      )}
    </Card>
  );
}
